import { AnimatePresence, motion } from "motion/react";
import { Loader2, WifiOff } from "lucide-react";
import { useMaintenanceStore, type ApiOutageCause } from "@/store/maintenance.store";

/**
 * Le texte suit la cause réelle de l'attente.
 *
 * « Mise à jour en cours » s'affichait aussi sur une simple coupure réseau : l'utilisateur attendait
 * un déploiement inexistant au lieu de regarder sa connexion. La correction a ensuite versé dans
 * l'excès inverse — pendant un déploiement backend, l'API devient totalement injoignable et le
 * bandeau envoyait vérifier une connexion parfaitement fonctionnelle.
 *
 * D'où trois messages plutôt que deux : on ne met en cause le réseau de l'utilisateur que lorsque
 * son propre appareil le déclare hors ligne. Dans le doute, on n'accuse personne et on se contente
 * d'annoncer la reprise automatique.
 */
const COPY: Record<ApiOutageCause, { title: string; description: string; spin: boolean }> = {
  maintenance: {
    title: "Mise à jour en cours",
    description: "Reconnexion automatique, un instant…",
    spin: true,
  },
  offline: {
    title: "Connexion interrompue",
    description: "Vérifie ta connexion internet, nouvelle tentative en cours…",
    spin: false,
  },
  unreachable: {
    title: "Connexion au serveur perdue",
    description: "Nouvelle tentative en cours…",
    spin: true,
  },
};

/**
 * Bannière non-bloquante affichée pendant une maintenance de déploiement ou une coupure réseau.
 * L'état est piloté par la couche API (`fetchWithRetry`) qui retente automatiquement les requêtes ;
 * l'utilisateur n'est pas déconnecté et peut continuer à consulter les données déjà chargées.
 */
export function MaintenanceOverlay() {
  const isMaintenance = useMaintenanceStore((s) => s.isMaintenance);
  const cause = useMaintenanceStore((s) => s.cause);

  // Repli sur « maintenance » : c'était le seul message avant cette distinction, mieux vaut le
  // comportement d'origine qu'un bandeau muet si la cause venait à manquer.
  const copy = COPY[cause ?? "maintenance"];
  const Icon = copy.spin ? Loader2 : WifiOff;

  return (
    <AnimatePresence>
      {isMaintenance && (
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          role="status"
          aria-live="polite"
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[2147483646] pointer-events-none px-4"
        >
          <div className="pointer-events-auto flex items-center gap-3 rounded-full bg-[#003366] text-white pl-4 pr-5 py-2.5 shadow-lg shadow-black/20 max-w-[92vw]">
            <Icon
              size={16}
              className={`shrink-0 text-amber-300 ${copy.spin ? "animate-spin" : ""}`}
            />
            <div className="min-w-0">
              <p className="text-sm font-bold leading-tight">{copy.title}</p>
              <p className="text-[11px] text-white/70 leading-tight truncate">{copy.description}</p>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
